import { useQuery } from '@tanstack/react-query';
import {
    Modal, Segmented, Space, Table,
    TableColumnsType, Tag, theme,
    Typography,
} from 'antd';
import {
    useCallback, useMemo, useState,
} from 'react';
import { Helmet } from 'react-helmet';

import { eventsQueries } from '@/api';
import {
    ActionViewButton, DefaultTable,
} from '@/components';
import { DEFAULT_ORDER_COLUMN } from '@/constants';
import {
    useCustomParams, usePopupQueryParams,
} from '@/hooks';
import {
    Event as DashboardEvent, EventsOrderColumn,
    SortDirection,
} from '@/types';
import {
    getCreatedAtColumn, getEventDescriptionColumn, getEventDetailsColumn, getEventPropertiesColumn, getEventUserColumn,
} from '@/utils';

const FIELD_LABELS: Record<string, string> = {
    // Загальні
    id: 'ID',
    created_at: 'Створено',
    updated_at: 'Оновлено',
    deleted_at: 'Видалено',
    is_active: 'Активний',
    priority: 'Пріоритет',
    comment: 'Коментар',

    // Проект
    title: 'Назва',
    description: 'Опис',
    regulation: 'Регламент',
    url: 'URL',
    fields: 'Поля',
    project_id: 'ID проекту',

    // Скрипт
    type: 'Тип',
    instructions: 'Інструкції',
    product_price: 'Ціна',
    product_sku: 'Артикул',
    product_id: 'ID товару',
    cross_sells: 'Крос-сейли',
    cross_sell_ids: 'ID крос-сейлів',
    source_script_id: 'Скрипт-джерело',

    // Користувач
    name: "Ім'я",
    nickname: 'Нікнейм',
    email: 'Email',
    password: 'Пароль',
    last_seen_at: 'Остання активність',
    roles: 'Ролі',
    projects: 'Проекти',

    // Заперечення
    body: 'Зміст',

    // Налаштування
    key: 'Ключ',
    content: 'Вміст',

    // Заявка підтримки
    subject: 'Тема',
    ticket_number: 'Номер заявки',
    status: 'Статус',
    details: 'Деталі',
    notes: 'Нотатки',
    user_id: 'ID користувача',

    // Медіа
    media_id: 'ID медіа',
    collection_name: 'Колекція медіа',
    file_name: 'Файл',

    // Інше
    ids: 'Порядок (ID)',
    acknowledged_changes_count: 'Підтверджено змін',
    value: 'Значення',
};

const SCRIPT_TYPE_LABELS: Record<string, string> = {
    default: 'Основний',
    'cross-sale': 'Крос-сейл',
};

const fieldLabel = (key: string) => FIELD_LABELS[key] || key;

const renderCellValue = (value: unknown, depth = 0): React.ReactNode => {
    if (value === undefined || value === null || value === '')
        return <Typography.Text type="secondary">—</Typography.Text>;

    if (typeof value === 'boolean')
        return <Tag color={value ? 'green' : 'default'}>{value ? 'Так' : 'Ні'}</Tag>;

    if (typeof value === 'number')
        return <Typography.Text>{value}</Typography.Text>;

    if (typeof value === 'string') {
        if (/<[a-z][\s\S]*>/i.test(value))
            return (
                <div
                    dangerouslySetInnerHTML={{ __html: value }}
                    style={{
                        fontSize: 12,
                        maxHeight: 160,
                        overflow: 'auto',
                        padding: '4px 8px',
                        borderRadius: 4,
                        border: '1px solid rgba(128,128,128,0.2)',
                        background: 'rgba(128,128,128,0.04)',
                        lineHeight: 1.5,
                    }}
                />
            );
        if (SCRIPT_TYPE_LABELS[value])
            return <Typography.Text>{SCRIPT_TYPE_LABELS[value]}</Typography.Text>;
        if (value.length > 200)
            return (
                <Typography.Text style={{ fontSize: 12 }}>
                    {value.slice(0, 200)}
                    <Typography.Text type="secondary">…</Typography.Text>
                </Typography.Text>
            );
        return <Typography.Text>{value}</Typography.Text>;
    }

    if (Array.isArray(value)) {
        if (value.length === 0)
            return <Typography.Text type="secondary">Порожній список</Typography.Text>;
        if (value.every(v => typeof v === 'string' || typeof v === 'number'))
            return (
                <Space size={4} wrap>
                    {(value as (string | number)[]).map((v, i) => (
                        <Tag key={i}>{v}</Tag>
                    ))}
                </Space>
            );
        if (value.every(v => typeof v === 'object' && v !== null)) {
            return (
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                    {value.map((item, i) => (
                        <div
                            key={i}
                            style={{
                                paddingLeft: 8,
                                borderLeft: '2px solid rgba(128,128,128,0.3)',
                            }}
                        >
                            {renderCellValue(item, depth + 1)}
                        </div>
                    ))}
                </Space>
            );
        }
        return <Typography.Text type="secondary">[{value.length} елементів]</Typography.Text>;
    }

    if (typeof value === 'object') {
        const entries = Object.entries(value as Record<string, unknown>);
        if (entries.length === 0)
            return <Typography.Text type="secondary">—</Typography.Text>;
        const visible = entries.slice(0, 5);
        const hidden = entries.length - visible.length;
        return (
            <Space direction="vertical" size={2} style={{ width: '100%' }}>
                {visible.map(([k, v]) => (
                    <div key={k} style={{ paddingLeft: depth > 0 ? 0 : undefined }}>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                            {fieldLabel(k)}:{' '}
                        </Typography.Text>
                        {renderCellValue(v, depth + 1)}
                    </div>
                ))}
                {hidden > 0 && (
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        …ще {hidden}
                    </Typography.Text>
                )}
            </Space>
        );
    }

    return <Typography.Text>{String(value)}</Typography.Text>;
};

type ViewMode = 'details' | 'code';

export const EventsPage = () => {
    const { orderColumn, orderDirection, currentPage, pageSize, search } = useCustomParams();
    const { token } = theme.useToken();
    const popupQuery = usePopupQueryParams();
    const [viewMode, setViewMode] = useState<ViewMode>('details');

    const eventsOrderColumn = useMemo(() => {
        if (orderColumn === DEFAULT_ORDER_COLUMN || !orderColumn) {
            return 'created_at' as EventsOrderColumn;
        }
        return orderColumn as EventsOrderColumn;
    }, [orderColumn]);

    const eventsOrderDirection = useMemo(() => {
        if (orderColumn === DEFAULT_ORDER_COLUMN || !orderColumn) {
            return 'desc' as SortDirection;
        }
        return orderDirection as SortDirection;
    }, [orderColumn, orderDirection]);

    const { data, isFetching } = useQuery(eventsQueries.getAllEvents({
        per_page: pageSize,
        current_page: currentPage,
        order_column: eventsOrderColumn,
        order_direction: eventsOrderDirection,
        search: search,
    }));
    const events = useMemo<DashboardEvent[]>(() => data?.items || [], [data?.items]);

    const activeEventId = useMemo(() => {
        if (popupQuery.mode !== 'details' || !popupQuery.itemId) {
            return null;
        }

        const parsedId = Number(popupQuery.itemId);
        return Number.isNaN(parsedId) ? null : parsedId;
    }, [popupQuery.itemId, popupQuery.mode]);

    const selectedEvent = useMemo(
        () => events.find((eventRecord) => eventRecord.id === activeEventId) || null,
        [activeEventId, events],
    );

    const { data: fallbackEventsData } = useQuery({
        ...eventsQueries.getAllEvents({
            per_page: -1,
            current_page: 1,
            order_column: eventsOrderColumn,
            order_direction: eventsOrderDirection,
            search: search,
        }),
        enabled: popupQuery.isMode('details') && activeEventId != null && !selectedEvent,
    });

    const fallbackEvent = useMemo(
        () => fallbackEventsData?.items.find((eventRecord) => eventRecord.id === activeEventId) || null,
        [activeEventId, fallbackEventsData?.items],
    );

    const resolvedEvent = selectedEvent || fallbackEvent || null;

    const handleOpenModal = useCallback((record: DashboardEvent) => {
        void popupQuery.open({
            mode: 'details',
            itemId: String(record.id),
        });
    }, [popupQuery]);

    const handleCloseModal = useCallback(() => {
        void popupQuery.close();
    }, [popupQuery]);

    const columns: TableColumnsType<DashboardEvent> = useMemo(() => [
        getEventDescriptionColumn((record) => handleOpenModal(record)),
        getEventUserColumn(),
        getCreatedAtColumn<DashboardEvent>(),
        getEventPropertiesColumn(),
        getEventDetailsColumn({
            render: (_: unknown, record: DashboardEvent) => (
                <ActionViewButton onClick={() => handleOpenModal(record)} />
            ),
        }),
    ], [handleOpenModal]);

    const formattedProperties = useMemo(() => {
        if (!resolvedEvent?.properties) return '';
        try {
            return JSON.stringify(resolvedEvent.properties, null, 2);
        } catch {
            return '';
        }
    }, [resolvedEvent]);

    const parsedProperties = useMemo(() => {
        if (!resolvedEvent?.properties || Object.keys(resolvedEvent.properties).length === 0) {
            return null;
        }

        const p = resolvedEvent.properties;
        const hasOld = Object.prototype.hasOwnProperty.call(p, 'old');
        const hasNew = Object.prototype.hasOwnProperty.call(p, 'new');
        const extraEntries = Object.entries(p).filter(([k]) => k !== 'old' && k !== 'new');

        return { hasOld, hasNew, old: p.old, new: p.new, extraEntries };
    }, [resolvedEvent]);

    const diffRows = useMemo(() => {
        if (!parsedProperties?.hasOld && !parsedProperties?.hasNew) return null;

        const oldObj = parsedProperties?.old;
        const newObj = parsedProperties?.new;

        if (typeof oldObj !== 'object' || typeof newObj !== 'object' || oldObj === null || newObj === null) {
            return null;
        }

        const allKeys = [...new Set([
            ...Object.keys(oldObj as Record<string, unknown>),
            ...Object.keys(newObj as Record<string, unknown>),
        ])];

        return allKeys.map((key) => {
            const oldVal = (oldObj as Record<string, unknown>)[key];
            const newVal = (newObj as Record<string, unknown>)[key];
            const changed = JSON.stringify(oldVal) !== JSON.stringify(newVal);
            return { key, field: fieldLabel(key), oldVal, newVal, changed };
        });
    }, [parsedProperties]);

    const preStyle = useMemo(() => ({
        background: token.colorFillTertiary,
        padding: token.padding,
        borderRadius: token.borderRadius,
        overflow: 'auto' as const,
        maxHeight: 400,
        color: token.colorText,
        border: `1px solid ${token.colorBorder}`,
        margin: 0,
        fontSize: 12,
        lineHeight: 1.6,
        fontFamily: 'monospace',
    }), [token]);

    const changedRowStyle = {
        background: token.colorWarningBg,
    };

    const renderDetailsContent = () => {
        if (!parsedProperties) {
            return (
                <Typography.Text type="secondary">Властивості відсутні</Typography.Text>
            );
        }

        const sections: React.ReactNode[] = [];

        if (diffRows) {
            sections.push(
                <div key="diff">
                    <Table
                        size="small"
                        pagination={false}
                        dataSource={diffRows}
                        rowKey="key"
                        onRow={(row) => ({
                            style: row.changed ? changedRowStyle : undefined,
                        })}
                        columns={[
                            {
                                title: 'Поле',
                                dataIndex: 'field',
                                key: 'field',
                                width: 150,
                                render: (v: string, row: typeof diffRows[0]) => (
                                    <Typography.Text strong={row.changed}>{v}</Typography.Text>
                                ),
                            },
                            {
                                title: 'Було',
                                dataIndex: 'oldVal',
                                key: 'oldVal',
                                render: renderCellValue,
                            },
                            {
                                title: 'Стало',
                                dataIndex: 'newVal',
                                key: 'newVal',
                                render: renderCellValue,
                            },
                        ]}
                    />
                </div>,
            );
        } else if (parsedProperties.hasOld || parsedProperties.hasNew) {
            const val = parsedProperties.hasNew ? parsedProperties.new : parsedProperties.old;
            const label = parsedProperties.hasNew ? 'Значення' : 'Було';

            if (typeof val === 'object' && val !== null && !Array.isArray(val)) {
                const rows = Object.entries(val as Record<string, unknown>).map(([k, v]) => ({
                    key: k,
                    field: fieldLabel(k),
                    value: v,
                }));
                sections.push(
                    <Table
                        key="single"
                        size="small"
                        pagination={false}
                        dataSource={rows}
                        rowKey="key"
                        columns={[
                            { title: 'Поле', dataIndex: 'field', key: 'field', width: 180 },
                            { title: label, dataIndex: 'value', key: 'value', render: renderCellValue },
                        ]}
                    />,
                );
            } else {
                sections.push(
                    <div key="single-primitive">
                        <Typography.Text type="secondary">{label}: </Typography.Text>
                        {renderCellValue(val)}
                    </div>,
                );
            }
        }

        if (parsedProperties.extraEntries.length > 0) {
            const rows = parsedProperties.extraEntries.map(([k, v]) => ({
                key: k,
                field: fieldLabel(k),
                value: v,
            }));
            sections.push(
                <div key="extra">
                    {diffRows && (
                        <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8, marginTop: 16 }}>
                            Додаткові дані
                        </Typography.Text>
                    )}
                    <Table
                        size="small"
                        pagination={false}
                        dataSource={rows}
                        rowKey="key"
                        showHeader={!diffRows}
                        columns={[
                            { title: 'Поле', dataIndex: 'field', key: 'field', width: 180 },
                            { title: 'Значення', dataIndex: 'value', key: 'value', render: renderCellValue },
                        ]}
                    />
                </div>,
            );
        }

        return <Space direction="vertical" size="small" style={{ width: '100%' }}>{sections}</Space>;
    };

    return (
        <>
            <Helmet>
                <title>Історія дій</title>
            </Helmet>

            <DefaultTable
                title="Історія дій"
                columns={columns}
                dataSource={data?.items || []}
                total={data?.total || 0}
                loading={isFetching}
                tableSize="small"
            />

            <Modal
                title={
                    <Space size="middle" align="center">
                        <span>Деталі події</span>
                        <Segmented
                            size="small"
                            value={viewMode}
                            onChange={(v) => setViewMode(v as ViewMode)}
                            options={[
                                { label: 'Детальніше', value: 'details' },
                                { label: 'Код', value: 'code' },
                            ]}
                        />
                    </Space>
                }
                open={popupQuery.isMode('details') && !!resolvedEvent}
                onCancel={handleCloseModal}
                footer={null}
                width={800}
            >
                {viewMode === 'details' ? (
                    renderDetailsContent()
                ) : (
                    formattedProperties
                        ? <pre style={preStyle}>{formattedProperties}</pre>
                        : <Typography.Text type="secondary">Властивості відсутні</Typography.Text>
                )}
            </Modal>
        </>
    );
};
